import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { useFonts } from "hooks";
import { ComponentIds } from "types/enums/componentIds";
import { useScreenIsAboveBreakpoint } from "./useScreenIsAboveBreakpoint";
import useTailwind from "./useTailwind";

const useAnchor = (selector: string, type: "className" | "id" = "className") => {
  const tailwind = useTailwind();
  const isDesktopView = useScreenIsAboveBreakpoint("lg");
  // asPath is used only to set initial path
  const { events, asPath } = useRouter();
  // Counter is used to force a re-render when the hash changes
  const [counter, setCounter] = useState(0);
  // Path is used to keep track of the current path
  // Next.js does not update the asPath when the hash changes
  const [path, setPath] = useState(asPath);

  const { fontsIsLoaded } = useFonts();

  const navbarHeightKey = isDesktopView ? "navbar-lg" : "navbar";
  const themeNavbarHeight = parseInt(tailwind.theme.height[navbarHeightKey], 10);
  const themeBannerHeight = parseInt(tailwind.theme.height.banner, 10);

  /**
   * This effect listens for hash changes and updates the path state
   * This is necessary because Next.js does not update the asPath when the hash changes
   */
  useEffect(() => {
    const listener = () => {
      setPath(window.location.href);
      setCounter((prev) => prev + 1);
    };

    window.addEventListener("popstate", listener, { passive: true });
    events.on("hashChangeComplete", listener);

    return () => {
      window.removeEventListener("popstate", listener);
      events.off("hashChangeComplete", listener);
    };
  });

  /**
   * This effect scrolls to the element with the given selector
   */
  useEffect(() => {
    const fullSelector = type === "className" ? `.${selector}` : `#${selector}`;

    const element = document.querySelector(fullSelector);

    if (!element) return;

    if (!fontsIsLoaded) return;

    if (path.includes(selector)) {
      // Adjust scroll position for navbar
      const navbarHeight = (() => {
        const isNavBarPresent = document.querySelector(`#${ComponentIds.MegaNavigation}`);

        if (isNavBarPresent) {
          return themeNavbarHeight;
        }

        return 0;
      })();

      // Adjust banner height
      const banner = (() => {
        const isBannerPresent = (document.querySelector(`#${ComponentIds.Banner}`)?.clientHeight ?? 0) > 0;

        if (isBannerPresent) {
          return themeBannerHeight;
        }

        return 0;
      })();

      const margin = isDesktopView ? 16 : 8;
      const totalOffset = navbarHeight + banner + margin;

      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.scrollY - totalOffset;

      window.scrollTo({ behavior: "smooth", top: offsetPosition });
    }
  }, [selector, fontsIsLoaded, path, type, isDesktopView, themeNavbarHeight, themeBannerHeight, counter]);
};

export { useAnchor };
